import { type FC, useRef, useState } from 'react';

import WebApp from '@twa-dev/sdk';
import clsx from 'clsx';

import { getIsSlowDevice } from '@/shared/helpers/get-is-slow-device';
import { isTMA } from '@/shared/helpers/is-tma';
import { Lottie } from '@/shared/hocs/lottieWrapper';

import closedLootbox from '@/assets/lotties/lootboxes/closed_lootbox.lottie?url';
import closedSpecialLootbox from '@/assets/lotties/lootboxes/closed-lootbox-premium.lottie?url';
import openedLootbox from '@/assets/lotties/lootboxes/opened_lootbox.lottie?url';
import openedSpecialLootbox from '@/assets/lotties/lootboxes/opened-lootbox-premium.lottie?url';

import s from './Lootbox.module.scss';

interface Props {
  className?: string;
  onOpen?: () => void | Promise<any>;
  onOpened?: () => void;
  isSpecial?: boolean;
}

export const Lootbox: FC<Props> = ({ className, onOpen, onOpened, isSpecial }) => {
  const containerRef = useRef<HTMLDivElement>(null);

  const [isOpened, setIsOpened] = useState(false);

  const handleClickLootbox = async () => {
    WebApp.HapticFeedback.impactOccurred('soft');

    try {
      await onOpen?.();

      setIsOpened(true);

      if (getIsSlowDevice()) {
        setTimeout(() => {
          WebApp.HapticFeedback.notificationOccurred('success');
          onOpened?.();
        }, 300);
      }
    } catch {
      WebApp.HapticFeedback.notificationOccurred('error');
    }
  };

  return (
    <div
      ref={containerRef}
      className={clsx(s.container, className)}
      onClick={isOpened ? undefined : handleClickLootbox}
    >
      {/* Render two lotties at the same time and hide one of them with CSS to avoid glitches while switching */}
      <Lottie
        key="opened"
        src={isSpecial ? openedSpecialLootbox : openedLootbox}
        className={clsx(s.box, s['opened-box'], !isOpened && s.hidden)}
        loop={false}
        autoplay={false}
        isPlaying={isOpened}
        onComplete={() => {
          WebApp.HapticFeedback.notificationOccurred('success');
          onOpened?.();
        }}
        onFrameChange={({ currentFrame }) => {
          if (!isTMA()) return;

          // haptic feedback while the box is shaking
          if (
            currentFrame > 30 &&
            currentFrame < 70 &&
            Math.floor(currentFrame) % 5 === 0
          ) {
            WebApp.HapticFeedback.impactOccurred('medium');
          }
        }}
      />
      <Lottie
        key="closed"
        src={isSpecial ? closedSpecialLootbox : closedLootbox}
        slowDeviceFrame={0}
        isPlaying={!isOpened}
        className={clsx(s.box, s['closed-box'], isOpened && s.hidden)}
        onFrameChange={({ currentFrame }) => {
          if (!isTMA()) return;

          // haptic feedback while the box is shaking
          if (
            currentFrame > 15 &&
            currentFrame < 70 &&
            Math.floor(currentFrame) % 9 === 0
          ) {
            WebApp.HapticFeedback.impactOccurred('soft');
          }
        }}
      />
    </div>
  );
};
